import { FeatureTypes } from "entity-app/constants";
import { useContext, useMemo, useState } from "react";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_APPLY_LINKING_CHANGES } from "services/graphQL/mutations";
import { ProjectContext } from "context/ProjectProvider";
import { message } from "utils/ciq-message";
import { ErrorMessages, SuccessMessages } from "constants/index";
import { TEditingEntity, TEntityLink, TMaterialLinkDirection } from "../models";
import { getFeatureDetail, translateEntityInfoIntoBlockCards } from "../utils";
import LinkedFeatureItems from "./linked-feature-items";
import { generateLinkingPayload } from "../utils/generate-payload";
import { resolveAiFeedbackPayload } from "../utils/ai-link-feedback";
import { submitLinkRecommenderFeedback } from "../services/link-recommender-feedback";
import { MUTATION_UPDATE_MATERIAL_TRACKING } from "../queries/mutation";
import { UnsaveConfirmModal } from "./unsave-confirm-dialog";
import ErrorPopUpComponent, { ErrorResponse } from "./error-pop-up";

function LeftSideLinkData({
  selectedEntityInfo,
  leftTabEntityTypes,
  editingEntity,
  setEditingEntity,
  refetchEntityInfo,
  featureTypeId,
  isAiRecommendationTabVisible = false
}: {
  selectedEntityInfo: TEntityLink | undefined;
  leftTabEntityTypes: FeatureTypes[];
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  refetchEntityInfo: () => Promise<void>;
  featureTypeId: FeatureTypes;
  isAiRecommendationTabVisible?: boolean;
}) {
  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);
  const [whileSaving, setWhileSaving] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorPayload, setErrorPayload] = useState<ErrorResponse | null>(null);

  const [linkingMutation] = useCIQMutation(MUTATION_APPLY_LINKING_CHANGES, {
    client: gqlClientForProject
  });
  const [updateMaterialTrackingMutation] = useCIQMutation(
    MUTATION_UPDATE_MATERIAL_TRACKING,
    {
      client: gqlClientForProject
    }
  );

  const handleEdit = (
    entity: Omit<TEditingEntity, "unlinkedItems">,
    materialLinkDirection?: TMaterialLinkDirection
  ) => {
    setEditingEntity({
      ...entity,
      unlinkedItems: [],
      materialLinkDirection
    });
  };

  const submitAiLinkFeedback = (forceAbandoned: boolean) => {
    if (!isAiRecommendationTabVisible || !editingEntity) return;
    const items = resolveAiFeedbackPayload({
      editingEntity,
      selectedEntityInfo,
      forceAbandoned
    });
    if (!items.length) return;
    submitLinkRecommenderFeedback({
      items,
      projectToken: tokenRetrievalState?.token
    })?.catch(() => {});
  };

  const handleCancel = () => {
    submitAiLinkFeedback(true);
    refetchEntityInfo();
    setEditingEntity(null);
  };

  const blockListData = useMemo(() => {
    if (selectedEntityInfo)
      return translateEntityInfoIntoBlockCards({
        entityInfo: selectedEntityInfo,
        leftTabEntityTypes
      });
    return [];
  }, [leftTabEntityTypes, selectedEntityInfo]);

  const handleSave = async () => {
    // Defensive guard: handleSave is only invoked from the save button (rendered
    // under `selectedEntityInfo &&` while editing) or UnsaveConfirmModal.onOk
    // (registered only when editingEntity is set); editingEntity itself is only
    // set via handleEdit, reachable only when selectedEntityInfo is truthy.
    /* v8 ignore next */
    if (!selectedEntityInfo || !editingEntity) return;
    setWhileSaving(true);

    const cardsFeatureName = editingEntity.featureDetail.feature;
    const oldEntityLinks =
      // cardsFeatureName comes from editingEntity which handleEdit built from a
      // blockListData card, so find always matches and .list is always present;
      // the ?. (find undefined) and ?? [] (list nullish) branches never hit.
      /* v8 ignore next 2 */
      blockListData.find((x) => x.featureDetail.feature === cardsFeatureName)
        ?.list ?? [];
    const newEntityLinks = editingEntity.list;

    const payload = generateLinkingPayload({
      selectedEntityInfo: {
        ids: selectedEntityInfo.ids,
        parentFeatureDetail: getFeatureDetail(
          selectedEntityInfo.feature_type_id
        ).feature,
        predecessors: selectedEntityInfo.predecessor_material_links
      },
      oldEntityLinks,
      newEntityLinks,
      cardsFeatureName,
      unlinkedBuffer: editingEntity.unlinkedItems,
      materialLinkDirection: editingEntity.materialLinkDirection
    });

    if (payload.offMaterialTrackingOfSubmittalIds.length) {
      // Make material tracking ON for these submittals
      await updateMaterialTrackingMutation({
        variables: {
          featureInstanceIds: payload.offMaterialTrackingOfSubmittalIds,
          material_tracking: true
        }
      });
    }

    const payloadData = {
      input: {
        inserts: payload.inserts,
        deletes: payload.deletes,
        updates: []
      },
      metadata: {
        request_source: "LINKING_PAGE"
      }
    };

    const linkingResponse = await linkingMutation(
      {
        variables: payloadData
      },
      { showError: false }
    );

    if (linkingResponse?.data) {
      message.success(SuccessMessages.SuccessfullyUpdate);

      submitAiLinkFeedback(false);

      await refetchEntityInfo();
      setEditingEntity(null);
    } else if (
      linkingResponse?.error &&
      linkingResponse?.error?.extensions?.code === "GraphCycleError"
    ) {
      setErrorPayload({ error: linkingResponse.error });
      setIsErrorModalOpen(true);
      setWhileSaving(false);
    } else {
      message.error(ErrorMessages.SomethingWentWrong);
    }
    setWhileSaving(false);
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-ciq-gray-50">
      {selectedEntityInfo &&
        blockListData.map(
          ({ list, featureDetail, entityDetail, parentEntity }) => {
            const isEditing =
              editingEntity !== null &&
              editingEntity.featureDetail.feature === featureDetail.feature;
            const isDisabled = editingEntity !== null && !isEditing;
            return (
              <LinkedFeatureItems
                key={featureDetail.feature}
                list={isEditing ? editingEntity.list : list}
                featureDetail={featureDetail}
                isEditing={isEditing}
                isDisabled={isDisabled}
                onEdit={(materialLinkDirection) =>
                  handleEdit(
                    {
                      list,
                      featureDetail,
                      entityDetail,
                      parentEntity,
                      height: 0
                    },
                    materialLinkDirection
                  )
                }
                onCancel={handleCancel}
                onSave={handleSave}
                editingEntity={editingEntity}
                setEditingEntity={setEditingEntity}
                refetchEntityInfo={refetchEntityInfo}
                selectedEntityInfo={selectedEntityInfo}
                featureTypeId={featureTypeId}
              />
            );
          }
        )}
      <UnsaveConfirmModal editingEntity={editingEntity} onSave={handleSave} />
      {isErrorModalOpen && errorPayload && (
        <ErrorPopUpComponent
          data={errorPayload}
          open={isErrorModalOpen}
          onProceed={() => setIsErrorModalOpen(false)}
          onClose={() => setIsErrorModalOpen(false)}
        />
      )}
      {whileSaving && (
        <div className="absolute inset-0 z-50 bg-black bg-opacity-0" />
      )}
    </div>
  );
}
export default LeftSideLinkData;
